import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, UserCheck, DollarSign, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const AdminOverview = () => {
  const { language } = useLanguage();
  const bn = language === "bn";
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeDietitians, setActiveDietitians] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [pendingVerifications, setPendingVerifications] = useState(0);

  useEffect(() => {
    Promise.all([
      supabase.from("profiles").select("id", { count: "exact", head: true }),
      supabase.from("profiles").select("id", { count: "exact", head: true }).eq("role", "dietitian").eq("verified", true),
      supabase.from("payments").select("amount").in("status", ["paid", "completed"]),
      supabase.from("profiles").select("id", { count: "exact", head: true }).in("role", ["dietitian", "doctor"]).eq("verified", false),
    ]).then(([usersRes, dietRes, paymentsRes, pendingRes]) => {
      setTotalUsers(usersRes.count ?? 0);
      setActiveDietitians(dietRes.count ?? 0);
      setTotalRevenue((paymentsRes.data ?? []).reduce((s: number, p: any) => s + (p.amount || 0), 0));
      setPendingVerifications(pendingRes.count ?? 0);
    }).finally(() => setLoading(false));
  }, []);

  const stats = [
    { icon: Users, labelEn: "Total Users", labelBn: "মোট ব্যবহারকারী", value: totalUsers },
    { icon: UserCheck, labelEn: "Active Dietitians", labelBn: "সক্রিয় পুষ্টিবিদ", value: activeDietitians },
    { icon: DollarSign, labelEn: "Total Revenue", labelBn: "মোট রাজস্ব", value: `৳${totalRevenue.toLocaleString()}` },
    { icon: TrendingUp, labelEn: "Pending", labelBn: "মুলতুবি", value: pendingVerifications },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <Card key={i} className="bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all hover:shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-primary" /></div>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
              <p className="text-2xl font-extrabold tracking-tight">{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{bn ? s.labelBn : s.labelEn}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-destructive/20 bg-destructive/5">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
          <div>
            <p className="text-sm font-bold">{pendingVerifications} {bn ? "যাচাই মুলতুবি আছে" : "verifications pending review"}</p>
            <p className="text-xs text-muted-foreground">{bn ? "অনুগ্রহ করে যাচাইকরণ পৃষ্ঠা পরীক্ষা করুন" : "Please check the verification page"}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverview;
